import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const EmptyCart = ({ recentlyViewed = [], popularProducts = [] }) => {
  return (
    <div className="bg-background min-h-screen pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Empty State */}
        <div className="text-center mb-12">
          <div className="w-32 h-32 mx-auto mb-6 bg-muted rounded-full flex items-center justify-center">
            <Icon name="ShoppingCart" size={64} className="text-muted-foreground" />
          </div>
          
          <h1 className="text-3xl font-heading font-semibold text-foreground mb-4">
            Your Cart is Empty
          </h1>
          
          <p className="text-lg text-muted-foreground mb-8 max-w-md mx-auto">
            Looks like you haven't added any beauty products to your cart yet. 
            Discover our amazing collection and find your perfect match!
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              iconName="Sparkles"
              iconPosition="left"
              onClick={() => window.location.href = '/product-catalog'}
            >
              Start Shopping
            </Button>
            
            <Button
              variant="outline"
              size="lg"
              iconName="Heart"
              iconPosition="left"
            >
              View Wishlist
            </Button>
          </div>
        </div>

        {/* Recently Viewed Products */}
        {recentlyViewed?.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-6">
              Continue Where You Left Off
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentlyViewed?.slice(0, 3)?.map((product) => (
                <div key={product?.id} className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-shadow flex flex-col">
                  <div className="relative aspect-square bg-muted overflow-hidden">
                    <Link to={`/product-details?id=${product?.id}`}>
                      <Image
                        src={product?.image}
                        alt={product?.imageAlt}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </Link>
                    
                    <div className="absolute top-3 right-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="bg-background/80 backdrop-blur-sm hover:bg-background"
                      >
                        <Icon name="Heart" size={16} />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 flex flex-col flex-grow">
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">
                      {product?.brand}
                    </p>
                    <Link to={`/product-details?id=${product?.id}`}>
                      <h3 className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-2 mb-2">
                        {product?.name}
                      </h3>
                    </Link>
                    
                    <div className="flex items-center gap-2 mb-auto">
                      <span className="text-lg font-semibold text-foreground">
                        ${product?.price?.toFixed(2)}
                      </span>
                      {product?.originalPrice && (
                        <span className="text-sm text-muted-foreground line-through">
                          ${product?.originalPrice?.toFixed(2)}
                        </span>
                      )}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      fullWidth
                      iconName="ShoppingCart"
                      iconPosition="left"
                      className="mt-3"
                    >
                      Add to Cart
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Popular Products */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Popular This Week
            </h2>
            <Link 
              to="/product-catalog"
              className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              View All
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularProducts?.map((product) => (
              <div key={product?.id} className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-shadow flex flex-col">
                <div className="relative aspect-square bg-muted overflow-hidden">
                  <Link to={`/product-details?id=${product?.id}`}>
                    <Image
                      src={product?.image}
                      alt={product?.imageAlt}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </Link>
                  
                  {product?.isPopular && (
                    <div className="absolute top-3 left-3">
                      <span className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-md">
                        Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="absolute top-3 right-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="bg-background/80 backdrop-blur-sm hover:bg-background"
                    >
                      <Icon name="Heart" size={16} />
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 flex flex-col flex-grow">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">
                    {product?.brand}
                  </p>
                  <Link to={`/product-details?id=${product?.id}`}>
                    <h3 className="text-sm font-medium text-foreground hover:text-primary transition-colors line-clamp-2 mb-2">
                      {product?.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center gap-1 mb-2">
                    <div className="flex items-center">
                      {[...Array(5)]?.map((_, i) => (
                        <Icon
                          key={i}
                          name="Star"
                          size={12}
                          className={i < Math.floor(product?.rating) ? 'text-warning fill-current' : 'text-muted-foreground'}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      ({product?.reviewCount})
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-auto">
                    <span className="text-lg font-semibold text-foreground">
                      ${product?.price?.toFixed(2)}
                    </span>
                    {product?.originalPrice && (
                      <span className="text-sm text-muted-foreground line-through">
                        ${product?.originalPrice?.toFixed(2)}
                      </span>
                    )}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="ShoppingCart"
                    iconPosition="left"
                    className="mt-3"
                  >
                    Add to Cart
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-card border border-border rounded-lg p-8">
          <h3 className="text-xl font-heading font-semibold text-foreground mb-6 text-center">
            Why Shop With Bounderlaster?
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name="Truck" size={24} className="text-primary" />
              </div>
              <h4 className="font-medium text-foreground mb-2">Free Shipping</h4>
              <p className="text-sm text-muted-foreground">
                Free shipping on orders over $50
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name="Shield" size={24} className="text-primary" />
              </div>
              <h4 className="font-medium text-foreground mb-2">Authentic Products</h4>
              <p className="text-sm text-muted-foreground">
                100% genuine beauty products guaranteed
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Icon name="RotateCcw" size={24} className="text-primary" />
              </div>
              <h4 className="font-medium text-foreground mb-2">Easy Returns</h4>
              <p className="text-sm text-muted-foreground">
                30-day hassle-free return policy
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmptyCart;